import { startOfWeek, addWeeks, format, parseISO } from 'date-fns';

/**
 * Get the start of the week containing the given date
 * @param date The date to get the week start for
 * @param weekStartsOn Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 */
export function getWeekStartDate(date: Date, weekStartsOn: number = 0): Date {
  return startOfWeek(date, { weekStartsOn: weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
}

/**
 * Get the end of the week containing the given date
 * @param weekStart The start date of the week
 */
export function getWeekEndDate(weekStart: Date): Date {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return weekEnd;
}

/**
 * Generate array of week periods starting from the current week
 * @param startDate Starting date (will use the start day of that week)
 * @param numWeeks Number of weeks to generate
 * @param weekStartsOn Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday), defaults to 0 (Sunday)
 * @returns Array of week period objects with start and end dates
 */
export function generateWeekPeriods(startDate: Date, numWeeks: number, weekStartsOn: number = 0): Array<{ start: Date; end: Date; date: string }> {
  // Normalize the start date to midnight local time to avoid timezone issues
  const normalizedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const firstWeekStart = getWeekStartDate(normalizedDate, weekStartsOn);
  const weeks: Array<{ start: Date; end: Date; date: string }> = [];

  for (let i = 0; i < numWeeks; i++) {
    const weekStart = addWeeks(firstWeekStart, i);
    const weekEnd = getWeekEndDate(weekStart);
    weeks.push({
      start: weekStart,
      end: weekEnd,
      date: format(weekStart, 'yyyy-MM-dd'), // Week start date in YYYY-MM-DD format
    });
  }

  return weeks;
}

/**
 * Check if a date falls within a week period
 * @param date The date to check
 * @param weekStart The start date of the week
 */
export function isDateInWeek(date: Date, weekStart: Date): boolean {
  const weekEnd = getWeekEndDate(weekStart);
  return date >= weekStart && date <= weekEnd;
}

/**
 * Parse ISO date string to Date object
 * Parses YYYY-MM-DD as local date to avoid timezone issues
 */
export function parseDate(dateString: string): Date {
  // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // For date-time strings, use parseISO
  return parseISO(dateString);
}

/**
 * Format Date object to YYYY-MM-DD string
 */
export function formatDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}
